import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Button, Icon, Input, message, Modal, Slider, Tooltip } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import api from '../../../../../services/apiInstance';
import { getRequiredVersionName } from '../../../../../services/utils';
import useLibraryContents from '../../hooks/contentManager/useLibraryContents';
import useContentActions from '../../hooks/contentManager/useContentActions';
import useResolutionLogic from '../../hooks/contentManager/useResolutionLogic';
import useModalState from '../../hooks/contentManager/useModalState';
import useContentOfCompaign from '../../hooks/contentManager/useContentOfCompaign';
import LibraryModals from '../contents_library/LibraryModals';
import ContentsOfCampaignModals from '../contents_of_campaign/ContentsOfCampaignModals';
import PlaceholderSlider from '../contents_of_campaign/PlaceholderSlider';
import ContentsOfCampaignHolder from '../../styles/contentsOfCampaignStyle';
import Broom from '../../../../../images/customIcons/broom';
import VersionTabsComponent from './VersionTabsComponent';
import ResolutionsPanelComponent from './ResolutionsPanelComponent';
import ModalContentComponent from '../../../../shared/components/ModalContentComponent';
import LibraryBrowserComponent from './LibraryBrowserComponent';
import { findFolderPath } from '../../../adLibrary/utils/folderTree';
import OptionModal from '../../../../shared/components/OptionModal';
import { HelpIcon } from '../../../../shared/components/HelpTooltipIcon';
import VersionAdsWellComponent, { WELL_DROPPABLE_ID } from './VersionAdsWellComponent';
import UploadAdModal from '../../../adLibrary/components/modals/UploadAdModal';

import Colors from '../../../../../themes/Colors';
export const isVersionFullyCovered = (listResolution = [], version = 'V1') =>
  listResolution.length > 0 &&
  listResolution.every((relation) =>
    (relation.contents || []).some((c) => (c.content_version_name || 'V1') === version)
  );

export const getOrphanContents = (listOfContentsInCampaign = [], listResolutionWithContent = []) => {
  const matchedIds = new Set();
  listResolutionWithContent.forEach((relation) => {
    (relation.contents || []).forEach((c) => matchedIds.add(c.id));
  });
  return listOfContentsInCampaign.filter((c) => !c.isPlaceholder && !matchedIds.has(c.id));
};

export const getUnmatchedVersions = (listOfContentsInCampaign = [], listResolutionWithContent = []) =>
  getOrphanContents(listOfContentsInCampaign, listResolutionWithContent)
    .map((c) => c.content_version_name || 'V1')
    .filter((v, i, arr) => arr.indexOf(v) === i);

export { getRequiredVersionName };

const NUMBERED_VERSION = /^V(\d+)$/i;

const nextVersionKey = (versions) => {
  const numbers = versions
    .map((v) => {
      const match = NUMBERED_VERSION.exec(v);
      return match ? parseInt(match[1], 10) : NaN;
    })
    .filter((n) => !isNaN(n));
  const maxNumbered = numbers.length ? Math.max(...numbers) : 0;
  let next = Math.max(versions.length + 1, maxNumbered + 1);
  while (versions.includes(`V${next}`)) next += 1;
  return `V${next}`;
};

const LibraryStepComponent = (props) => {
  const { t } = useTranslation();
  const {
    cart,
    listOfContentsInCampaign,
    setListOfContentsInCampaign,
    listResolutionWithContent,
    setListResolutionWithContent,
    customContents,
    updateCustomContents,
    updateListOfContentsInCampaign,
    toleranceResolution,
    toleranceResolutionInit,
    setToleranceResolution,
    editCampaignActive,
    currentCampaign,
    creationType,
    placeholders,
    fetchingPlaceholders,
    isSuperUser,
    isPaymentStatusApproved,
    initialBackup,
    hasInitialBackup,
    setInitialBackup,
    restoreCampaignToOriginalState,
    clearCustomContents,
    cleanListOfContentsInCampaign,
    remplaceContentForUniqueVersion,
    deleteContentFromCampaign,
    applyRelationOnScreens,
    setListOfPlaceholdersInCampaign,
    setListResolutionWithPlaceholders,
    getPlaceholders,
    reportSingleData,
    libraryContents,
    loadingContentsbyCompany,
    uploadingAds,
    uploadAds,
    categories,
    loadingCategories,
    getCategories,
  } = props;

  const [manualVersions, setManualVersions] = useState([]);
  const [requiredVersionNameOverride, setRequiredVersionNameOverride] = useState(null);
  const [pendingRemoveVersion, setPendingRemoveVersion] = useState(null);
  const [previewingContent, setPreviewingContent] = useState(null);
  // Version renaming state disabled; kept for a future iteration.
  // const [renamingVersion, setRenamingVersion] = useState(null);
  // const [renameValue, setRenameValue] = useState('');

  const modalState = useModalState({ deleteContentFromCampaign });

  const libraryState = useLibraryContents({
    ...props,
    cardWidth: 220,
    cardGap: 16,
    reservedSlots: 0,
    initialVersion: getRequiredVersionName(listOfContentsInCampaign)
  });
  const contentActions = useContentActions(
    { ...props, groupedDisplays: null, contentSelected: null, exitModal: () => {} },
    libraryState
  );

  const handleUploadFiles = (files, folderId) => {
    uploadAds(files, { folderId: folderId || (libraryState.selectTagContent && libraryState.selectTagContent.key) });
  };

  // The wizard uses the Ad Library's own upload modal so this step gets the same
  // capabilities as the library itself: name, category and DCO ads.
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const currentFolderId = libraryState.selectTagContent && libraryState.selectTagContent.key;

  useEffect(() => {
    if (getCategories && (!categories || categories.length === 0)) getCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadModalSubmit = (files, meta) => {
    setUploadModalOpen(false);
    uploadAds(files, { ...meta, folderId: meta.folderId || currentFolderId });
  };

  const [wellUploading, setWellUploading] = useState(false);

  const getFileDimensions = (file) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const cleanup = (dimensions) => {
        URL.revokeObjectURL(url);
        resolve(dimensions);
      };
      if (file.type.startsWith('video')) {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => cleanup({ width: video.videoWidth, height: video.videoHeight });
        video.onerror = () => cleanup(null);
        video.src = url;
      } else {
        const img = new Image();
        img.onload = () => cleanup({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => cleanup(null);
        img.src = url;
      }
    });

  const [pendingReplaceConfirm, setPendingReplaceConfirm] = useState(null);
  const confirmReplaceUpload = () => new Promise((resolve) => setPendingReplaceConfirm({ resolve }));

  const handleWellDropFiles = async (files) => {
    const folderId = libraryState.selectTagContent && libraryState.selectTagContent.key;
    setWellUploading(true);
    try {
      const filesToUpload = [];
      for (const file of files) {
        const dimensions = await getFileDimensions(file);
        const conflicts =
          dimensions && contentActions.wouldConflictInVersion(dimensions.width, dimensions.height, activeVersion);
        if (conflicts && !(await confirmReplaceUpload())) continue;
        filesToUpload.push(file);
      }
      if (!filesToUpload.length) return;

      const results = await Promise.all(
        filesToUpload.map((file) =>
          api.createContent({
            file,
            name: file.name.replace(/\.[^/.]+$/, ''),
            tags: folderId ? [folderId] : [],
          })
        )
      );
      const created = results.filter((resp) => resp.ok).map((resp) => resp.data);
      if (created.length < results.length) {
        message.error(t('An error occurred, please try again'));
      }
      created.forEach((content) => contentActions.addContents(content));
      if (created.length) libraryState.getLibraryContents(1, libraryState.selectTagContent);
    } finally {
      setWellUploading(false);
    }
  };

  const wasUploadingAds = useRef(false);
  useEffect(() => {
    if (wasUploadingAds.current && !uploadingAds) {
      libraryState.getLibraryContents(1, libraryState.selectTagContent);
    }
    wasUploadingAds.current = uploadingAds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadingAds]);

  const resolution = useResolutionLogic({
    cart,
    listOfContentsInCampaign,
    customContents,
    toleranceResolution,
    listResolutionWithContent,
    editCampaignActive,
    currentCampaign,
    creationType,
    placeholders,
    isSuperUser,
    isPaymentStatusApproved,
    toleranceResolutionInit,
    initialBackup,
    hasInitialBackup,
    groupedDisplays: null,
    filterByContent: null,
    selectedContent: null,
    fromModal: null,
    groupName: null,
    setListResolutionWithContent,
    setListOfContentsInCampaign,
    updateCustomContents,
    remplaceContentForUniqueVersion,
    applyRelationOnScreens,
    setToleranceResolution,
    setInitialBackup,
    setListOfPlaceholdersInCampaign,
    setListResolutionWithPlaceholders,
    getPlaceholders,
    restoreCampaignToOriginalState,
    clearCustomContents,
    cleanListOfContentsInCampaign,
    deletingContentFromLibrary: modalState.deletingContentFromLibrary,
    setDeletingContentFromLibrary: modalState.setDeletingContentFromLibrary,
    reportSingleData,
  });

  const currentFolderPath = useMemo(
    () =>
      libraryState.selectTagContent && libraryState.selectTagContent.key != null
        ? findFolderPath(libraryState.tagsContents, libraryState.selectTagContent.key)
        : [],
    [libraryState.tagsContents, libraryState.selectTagContent]
  );
  const currentFolder = currentFolderPath.length ? currentFolderPath[currentFolderPath.length - 1] : null;
  const displayedFolders = currentFolder ? currentFolder.children || [] : libraryState.tagsContents;

  const requiredVersionName = useMemo(
    () => requiredVersionNameOverride || getRequiredVersionName(listOfContentsInCampaign),
    [requiredVersionNameOverride, listOfContentsInCampaign]
  );

  const contentOfCampaign = useContentOfCompaign({
    listOfContentsInCampaign,
    customContents,
    initialBackup,
    editCampaignActive,
    hasInitialBackup,
    groupedDisplays: null,
    cart,
    listResolutionWithContent,
    setListResolutionWithContent,
    updateListOfContentsInCampaign,
    updateCustomContents,
    applyRelationOnScreens,
    restoreCampaignToOriginalState,
    clearCustomContents,
    cleanListOfContentsInCampaign,
    showModalCleanContent: modalState.showModalCleanContent,
    setResolutionFilterIndex: resolution.setResolutionFilterIndex,
    setVersionFilter: resolution.setVersionFilter,
    requiredVersionName,
    contentToCloneName: modalState.contentToCloneName,
    cancelModalCloneNameContent: modalState.cancelModalCloneNameContent,
  });

  const activeVersion = libraryState.selectVersion || requiredVersionName;

  useEffect(() => {
    resolution.setVersionFilter(libraryState.selectVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryState.selectVersion]);

  const handleSelectVersion = (version) => {
    libraryState.onChangeVersion(version);
  };

  const handleUnlinkContent = (content, relation) => {
    const displayIds = relation.displays.map((d) => d.id);
    setListOfContentsInCampaign(
      listOfContentsInCampaign.map((c) =>
        c.id === content.id && (c.content_version_name || 'V1') === (content.content_version_name || 'V1')
          ? { ...c, excludedDisplayIds: [...new Set([...(c.excludedDisplayIds || []), ...displayIds])] }
          : c
      )
    );
  };

  const handleRelinkContent = (content, relation) => {
    const displayIds = relation.displays.map((d) => d.id);
    setListOfContentsInCampaign(
      listOfContentsInCampaign.map((c) =>
        c.id === content.id && (c.content_version_name || 'V1') === (content.content_version_name || 'V1')
          ? { ...c, excludedDisplayIds: (c.excludedDisplayIds || []).filter((id) => !displayIds.includes(id)) }
          : c
      )
    );
  };

  const isFirstToleranceRender = useRef(true);
  useEffect(() => {
    if (isFirstToleranceRender.current) {
      isFirstToleranceRender.current = false;
      return;
    }
    if (!editCampaignActive || !isPaymentStatusApproved) return;
    const timer = setTimeout(() => resolution.applyTolerance(), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toleranceResolution]);

  const prevContentsRef = useRef(listOfContentsInCampaign);
  useEffect(() => {
    const prevContents = prevContentsRef.current;
    prevContentsRef.current = listOfContentsInCampaign;
    if (!editCampaignActive || !isPaymentStatusApproved) return;
    if (listOfContentsInCampaign.length <= prevContents.length) return;
    const prevKeys = new Set(prevContents.map((c) => `${c.id}_${c.content_version_name}`));
    const newItems = listOfContentsInCampaign.filter(
      (c) => !prevKeys.has(`${c.id}_${c.content_version_name}`)
    );
    if (newItems.some((item) => item._isFromReplacement)) return;
    const timer = setTimeout(() => {
      resolution.applyTolerance();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfContentsInCampaign.length]);

  const handleToleranceChange = (value) => {
    resolution.setTolerance(value);
  };

  const versions = useMemo(() => {
    const fromContent = listOfContentsInCampaign
      .map((c) => c.content_version_name || 'V1')
      .filter((v, i, arr) => arr.indexOf(v) === i);
    return [requiredVersionName, ...fromContent, ...manualVersions, activeVersion].filter(
      (v, i, arr) => arr.indexOf(v) === i
    );
  }, [listOfContentsInCampaign, manualVersions, requiredVersionName, activeVersion]);

  const versionCounts = useMemo(() => {
    const counts = {};
    listOfContentsInCampaign.forEach((c) => {
      const v = c.content_version_name || 'V1';
      counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  }, [listOfContentsInCampaign]);

  const handleAddVersion = () => {
    const next = nextVersionKey(versions);
    setManualVersions((prev) => [...prev, next]);
    handleSelectVersion(next);
  };

  const unmatchedVersions = useMemo(
    () => new Set(getUnmatchedVersions(listOfContentsInCampaign, listResolutionWithContent)),
    [listOfContentsInCampaign, listResolutionWithContent]
  );

  const orphanContentIds = useMemo(
    () => new Set(getOrphanContents(listOfContentsInCampaign, listResolutionWithContent).map((c) => c.id)),
    [listOfContentsInCampaign, listResolutionWithContent]
  );

  const inactiveVersions = useMemo(() => {
    const set = new Set();
    versions.forEach((v) => {
      if (v === requiredVersionName) return;
      const relationsInVersion = customContents.filter(
        (cc) => cc.content && (cc.content.content_version_name || 'V1') === v
      );
      if (relationsInVersion.length > 0 && !relationsInVersion.some((cc) => cc.active === 1)) {
        set.add(v);
      }
    });
    return set;
  }, [versions, customContents, requiredVersionName]);

  const handleRemoveVersion = (version) => {
    if (version === requiredVersionName) return;
    const willHardDelete = contentActions.willHardDeleteVersion(version);
    setPendingRemoveVersion({ version, willHardDelete });
  };

  const handlePreviewContent = (content, relation) => {
    setPreviewingContent({ content, relation });
  };

  const confirmRemoveVersion = () => {
    const { version, willHardDelete } = pendingRemoveVersion;
    contentActions.deleteVersion(version);
    if (willHardDelete) {
      setManualVersions((prev) => prev.filter((v) => v !== version));
      if (activeVersion === version) handleSelectVersion(requiredVersionName);
    }
    setPendingRemoveVersion(null);
  };

  // Version renaming is disabled; names stay as the automatic V1, V2... Kept for later.
  // const openRenameVersion = (version) => {
  //   setRenamingVersion(version);
  //   setRenameValue(version);
  // };
  //
  // const cancelRenameVersion = () => {
  //   setRenamingVersion(null);
  //   setRenameValue('');
  // };
  //
  // const confirmRenameVersion = () => {
  //   const oldName = renamingVersion;
  //   const trimmed = renameValue.trim();
  //   if (!oldName || !trimmed || trimmed === oldName) {
  //     cancelRenameVersion();
  //     return;
  //   }
  //   if (versions.includes(trimmed)) {
  //     message.error(t('A version with that name already exists.'));
  //     return;
  //   }
  //   if (NUMBERED_VERSION.test(trimmed)) {
  //     message.error(t('Names like V1, V2... are reserved for automatic versions and can\'t be used to rename.'));
  //     return;
  //   }
  //
  //   const isRenamingRequiredVersion = oldName === requiredVersionName;
  //   if (isRenamingRequiredVersion) setRequiredVersionNameOverride(trimmed);
  //
  //   listOfContentsInCampaign
  //     .filter((c) => (c.content_version_name || 'V1') === oldName)
  //     .forEach((c) =>
  //       updateListOfContentsInCampaign({
  //         ...c,
  //         content_version_name: trimmed,
  //         ...(isRenamingRequiredVersion ? { isRequiredVersion: true } : {}),
  //       })
  //     );
  //
  //   const updatedList = listResolutionWithContent.map((relation) => ({
  //     ...relation,
  //     contents: relation.contents.map((item) =>
  //       (item.content_version_name || 'V1') === oldName
  //         ? {
  //             ...item,
  //             content_version_name: trimmed,
  //             ...(isRenamingRequiredVersion ? { isRequiredVersion: true } : {}),
  //           }
  //         : item
  //     ),
  //   }));
  //   setListResolutionWithContent(updatedList);
  //
  //   const updatedCustomContents = customContents.map((cc) =>
  //     cc.content && (cc.content.content_version_name || 'V1') === oldName
  //       ? {
  //           ...cc,
  //           content: {
  //             ...cc.content,
  //             content_version_name: trimmed,
  //             ...(isRenamingRequiredVersion ? { isRequiredVersion: true } : {}),
  //           },
  //         }
  //       : cc
  //   );
  //   updateCustomContents(updatedCustomContents);
  //
  //   setManualVersions((prev) => prev.map((v) => (v === oldName ? trimmed : v)));
  //   if (libraryState.selectVersion === oldName) handleSelectVersion(trimmed);
  //
  //   cancelRenameVersion();
  // };

  const rawContents = (libraryContents && libraryContents.data) || [];

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const handleDragEnd = ({ active, over }) => {
    if (!over || over.id !== WELL_DROPPABLE_ID) return;
    if (inactiveVersions.has(activeVersion)) return;
    const rawContent = active.data.current && active.data.current.rawContent;
    if (rawContent) contentActions.addContents(rawContent);
  };

  if (!editCampaignActive && !creationType) {
    return null;
  }

  if (creationType !== 'createCampaign' && !editCampaignActive) {
    return (
      <div
        className="library-step"
        style={{
          background: Colors.snow,
          borderRadius: 4,
          border: 0,
          padding: 24,
          boxShadow: 'rgba(0, 0, 0, 0.25) 1px 2px 4px 0px',
          display: 'flow-root',
        }}
      >
        <ContentsOfCampaignHolder withCardInRelation={130}>
          <PlaceholderSlider
            listResolution={resolution.listResolution}
            fetchingPlaceholders={fetchingPlaceholders}
            placeholders={placeholders}
          />
        </ContentsOfCampaignHolder>
      </div>
    );
  }

  const isProposalMode = !editCampaignActive && creationType === 'makeProposal';

  const showVersionSelect =
    !editCampaignActive || (editCampaignActive && currentCampaign && currentCampaign.status !== 'proposal');

  const panelStyle = {
    background: Colors.snow,
    borderRadius: 4,
    marginBottom: 24,
    border: 0,
    padding: 24,
    boxShadow: 'rgba(0, 0, 0, 0.25) 1px 2px 4px 0px',
  };

  return (
    <DndContext sensors={dndSensors} onDragEnd={handleDragEnd}>
    <div className="library-step">
      <div style={panelStyle}>
        <LibraryBrowserComponent
          isSuperUser={isSuperUser}
          containerRef={libraryState.containerRef}
          rawContents={rawContents}
          loading={loadingContentsbyCompany}
          folders={displayedFolders}
          selectedFolder={libraryState.selectTagContent}
          folderPath={currentFolderPath}
          onSelectFolder={(folderId) => {
            const folder =
              folderId != null
                ? [...currentFolderPath, ...displayedFolders].find((f) => f.id === folderId)
                : null;
            libraryState.onChangeFolder(folder ? { key: folder.id, label: folder.name } : { key: null });
          }}
          onSearch={(value) => libraryState.onSearch({ target: { value } })}
          onAddContent={contentActions.addContents}
          onOpenUploadModal={() => setUploadModalOpen(true)}
          onAddAllFromFolder={contentActions.triggerAddAllFromFolder}
          loadingAddAll={contentActions.loadingAddAll}
          isFolderUsed={contentActions.isFolderUsed}
          totalContent={libraryState.totalContent}
          pageContents={libraryState.pageContents}
          lengthPage={libraryState.lengthPage}
          onPageChange={libraryState.changePageContents}
          onPreviewAd={contentActions.showModalContent}
          typeFilter={libraryState.typeFilter}
          onTypeFilterChange={libraryState.onTypeFilterChange}
          sort={libraryState.sort}
          onSortChange={libraryState.onSortChange}
          onUploadFiles={handleUploadFiles}
          uploading={uploadingAds}
          checkContentsAdded={contentActions.checkContentsAdded}
          canUploadToFolder={libraryState.isOwnerDefaultContent(libraryState.selectTagContent)}
          onCreateFolder={libraryState.createFolder}
          onRenameFolder={libraryState.renameFolder}
          onDeleteFolders={libraryState.deleteFolders}
          folderSubmitting={libraryState.folderSubmitting}
          rowCount={libraryState.rowCount}
          rowOptions={libraryState.rowOptions}
          onRowCountChange={libraryState.handleRowCountChange}
          isMobile={libraryState.isMobile()}
          activeVersion={activeVersion}
          onSelectVersion={handleSelectVersion}
          showVersionSelect={showVersionSelect}
        />
      </div>

      <div style={panelStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ color: '#1890ff', margin: 0 }}>{t('Ads for the campaign')}</h2>
            <Tooltip title={<Trans i18nKey="Version explain" components={{ icon: <HelpIcon /> }} />}>
              <a
                href="https://www.youtube.com/watch?v=qRVte699bYA"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon type="exclamation-circle" theme="filled" style={{ color: '#faad14', fontSize: 14 }} />
              </a>
            </Tooltip>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: `1px solid ${Colors.borderStrong}`,
                borderRadius: 8,
                padding: '4px 16px',
                background: Colors.snow,
              }}
            >
              <span>{t('Tolerance')}</span>
              <Slider
                style={{ width: 100, margin: 0 }}
                min={0}
                max={isSuperUser ? 50 : 25}
                value={toleranceResolution}
                onChange={handleToleranceChange}
                tooltipVisible={false}
              />
              <span style={{ fontWeight: 600 }}>{toleranceResolution}%</span>
            </div>
            <Tooltip
              title={
                !contentOfCampaign.clearProgressDisabled()
                  ? t('Clear all contents added to the campaign, clearing its progress')
                  : ''
              }
            >
              <Button
                className="secondary buttonContent standard"
                onClick={modalState.showModalCleanContent}
                disabled={contentOfCampaign.clearProgressDisabled()}
              >
                <Broom />
                <span style={{ marginLeft: 8 }}>{t('Clear progress')}</span>
              </Button>
            </Tooltip>
          </div>
        </div>

        <VersionTabsComponent
          versions={versions}
          activeVersion={activeVersion}
          counts={versionCounts}
          inactiveVersions={inactiveVersions}
          unmatchedVersions={unmatchedVersions}
          requiredVersionName={requiredVersionName}
          onSelectVersion={handleSelectVersion}
          onAddVersion={handleAddVersion}
          onRemoveVersion={handleRemoveVersion}
          // onRenameVersion={openRenameVersion} // renaming disabled; kept for a future iteration
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 16 }}>
          <div>
            <VersionAdsWellComponent
              heading={t('Ads in {{version}}', { version: activeVersion })}
              contents={resolution.listOfContentsInCampaignFiltered}
              onRename={contentOfCampaign.handleContent}
              onCloneName={modalState.showModalCloneNameContent}
              onPreview={handlePreviewContent}
              onDeleteRequest={modalState.showModalDeleteContentRelation}
              listResolution={resolution.listResolution}
              isPaymentStatusApproved={isPaymentStatusApproved}
              customContents={customContents}
              readOnly={inactiveVersions.has(activeVersion)}
              onUploadFiles={handleWellDropFiles}
              uploading={wellUploading}
              orphanContentIds={orphanContentIds}
            />
          </div>

          <div>
            {!isProposalMode ? (
              <ResolutionsPanelComponent
                listResolution={resolution.listResolution}
                activeVersion={activeVersion}
                versions={versions}
                onSelectVersion={handleSelectVersion}
                onUnlinkContent={handleUnlinkContent}
                onRelinkContent={handleRelinkContent}
                onPreviewContent={handlePreviewContent}
                versionContents={listOfContentsInCampaign.filter(
                  (c) => (c.content_version_name || 'V1') === activeVersion
                )}
                isPaymentStatusApproved={isPaymentStatusApproved}
                hasDifferentResolution={resolution.hasDifferentResolution}
              />
            ) : (
              <PlaceholderSlider
                listResolution={resolution.listResolution}
                fetchingPlaceholders={fetchingPlaceholders}
                placeholders={placeholders}
              />
            )}
          </div>
        </div>
      </div>

      <UploadAdModal
        visible={uploadModalOpen}
        folders={libraryState.tagsContents}
        currentTag={currentFolderId}
        uploading={uploadingAds}
        categories={categories}
        loadingCategories={loadingCategories}
        isSuperUser={isSuperUser}
        onSubmit={handleUploadModalSubmit}
        onCancel={() => setUploadModalOpen(false)}
      />

      <OptionModal
        title={t('Atention!')}
        body={
          pendingRemoveVersion && pendingRemoveVersion.willHardDelete
            ? t('This ad will be unlinked from the group of screens for this resolution.')
            : t('This version will be deactivated. Its remaining outputs will be redistributed equitably among the other active versions; outputs already delivered are preserved.')
        }
        visible={!!pendingRemoveVersion}
        okText={t('Continue')}
        cancelText={t('Cancel')}
        onOk={confirmRemoveVersion}
        onCancel={() => setPendingRemoveVersion(null)}
      />

      <OptionModal
        title={t('Remplace version')}
        body={t('A version with that resolution already exists, you want to replace the content')}
        visible={!!pendingReplaceConfirm}
        okText={t('Remplace')}
        cancelText={t('Cancel')}
        onOk={() => {
          pendingReplaceConfirm.resolve(true);
          setPendingReplaceConfirm(null);
        }}
        onCancel={() => {
          pendingReplaceConfirm.resolve(false);
          setPendingReplaceConfirm(null);
        }}
      />

      <ModalContentComponent
        visible={!!previewingContent}
        content={previewingContent && previewingContent.content}
        relation={previewingContent && previewingContent.relation}
        onCancel={() => setPreviewingContent(null)}
      />

      {/* Version renaming modal disabled; kept for a future iteration.
      <Modal
        title={t('Rename version')}
        visible={!!renamingVersion}
        onOk={confirmRenameVersion}
        onCancel={cancelRenameVersion}
        okButtonProps={{ disabled: !renameValue.trim() }}
        destroyOnClose
      >
        <div style={{ padding: 21 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('Version name')}</label>
          <Input
            autoFocus
            maxLength={20}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onPressEnter={confirmRenameVersion}
          />
        </div>
      </Modal>
      */}

      <LibraryModals
        visibleModalContent={contentActions.visibleModalContent}
        visibleModalDeleteContent={contentActions.visibleModalDeleteContent}
        isModalOpen={contentActions.isModalOpen}
        isModalCreateVersionOpen={contentActions.isModalCreateVersionOpen}
        visibleCanvaModal={contentActions.visibleCanvaModal}
        alertModalVisible={contentActions.alertModalVisible}
        conflictModalOpen={contentActions.conflictModalOpen}
        dynamicAlertVisible={contentActions.dynamicAlertVisible}
        closeDynamicAlert={contentActions.closeDynamicAlert}
        contentModal={contentActions.contentModal}
        selectTagContent={libraryState.selectTagContent}
        getChangeAdDescription={contentActions.getChangeAdDescription}
        conflictGroups={contentActions.conflictGroups}
        existingByResolution={contentActions.existingByResolution}
        paymentStatus={contentActions.paymentStatus}
        cancelModalContent={contentActions.cancelModalContent}
        updateContent={contentActions.updateContent}
        showModalDeleteContent={contentActions.showModalDeleteContent}
        replaceVersion={contentActions.replaceVersion}
        handleCancel={contentActions.handleCancel}
        createVersion={contentActions.createVersion}
        handleModalCreateVersionCancel={contentActions.handleModalCreateVersionCancel}
        handleChangeAd={contentActions.handleChangeAd}
        setAlertModalVisible={contentActions.setAlertModalVisible}
        setVisibleCanvaModal={contentActions.setVisibleCanvaModal}
        confirmAddAll={contentActions.confirmAddAll}
        cancelAddAll={contentActions.cancelAddAll}
      />

      <ContentsOfCampaignModals
        visibleModalDeleteContentRelation={modalState.visibleModalDeleteContentRelation}
        showModalDeleteContentRelation={modalState.showModalDeleteContentRelation}
        deleteContentOfListRelation={modalState.deleteContentOfListRelation}
        visibleModalCleanContent={modalState.visibleModalCleanContent}
        showModalCleanContent={modalState.showModalCleanContent}
        cleanProgress={contentOfCampaign.cleanProgress}
        visibleModalCloneNameContent={modalState.visibleModalCloneNameContent}
        cancelModalCloneNameContent={modalState.cancelModalCloneNameContent}
        cloneNameToAllVersions={contentOfCampaign.cloneNameToAllVersions}
        visibleModalContent={modalState.visibleModalContent}
        cancelModalContent={modalState.cancelModalContent}
        contentModal={modalState.contentModal}
      />
    </div>
    </DndContext>
  );
};

export default LibraryStepComponent;
